import React from 'react';
import { FaUserTag } from 'react-icons/fa';
import styled from 'styled-components';
import { Select } from '../../../../common/Select';
import { ContactDesignation, CONTACT_DESIGNATION_LABELS } from '../../../../../types/contact';
import { ExtendedMarkerData } from '../../../../../types/marker';
import { v4 as uuidv4 } from 'uuid';

interface ContactDesignationSelectProps {
  formData: ExtendedMarkerData;
  setFormData: React.Dispatch<React.SetStateAction<ExtendedMarkerData>>;
  error?: string;
  isReadOnly?: boolean;
}

const DesignationField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  background-color: ${({ theme }) => theme.colors.backgroundAlt || '#f5f5f5'};
  border-radius: 4px;
  
  .field-icon {
    color: ${({ theme }) => theme.colors.primary};
    flex-shrink: 0;
    font-size: 14px;
  }
  
  /* Ajuster la taille du select pour correspondre aux autres champs */
  select {
    font-size: 13px;
    padding: 6px 10px;
    height: auto;
    min-height: 32px;
  }
  
  /* Responsive: sur mobile, l'icône reste visible */
  @media (max-width: 768px) {
    gap: 6px;
    padding: 6px 8px;
    
    .field-icon {
      font-size: 12px;
    }
    
    select {
      font-size: 12px;
      padding: 5px 8px;
      min-height: 28px;
    }
  }
`;

/**
 * Options pour le select de désignation
 */
const DESIGNATION_OPTIONS = Object.entries(CONTACT_DESIGNATION_LABELS).map(([value, label]) => ({
  value,
  label
}));

/**
 * Composant pour sélectionner la désignation/classification d'un contact
 * Gère automatiquement la synchronisation entre formData et contacts[0]
 */
export const ContactDesignationSelect: React.FC<ContactDesignationSelectProps> = ({
  formData,
  setFormData,
  error,
  isReadOnly = false
}) => {
  // Récupérer la désignation actuelle depuis le premier contact ou utiliser 'contact' par défaut
  const currentDesignation = formData.contacts?.[0]?.designation || 'contact';

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDesignation = e.target.value as ContactDesignation;
    const updatedContacts = [...(formData.contacts || [])];
    
    // Créer ou mettre à jour le premier contact
    if (updatedContacts.length === 0) {
      // Créer un nouveau contact avec toutes les données existantes
      const newContact = {
        id: uuidv4(),
        name: formData.name || '',
        firstName: formData.firstName || '',
        type: 'owner' as const,
        address: formData.contact_address || '',
        phone: formData.phone || '',
        email: formData.email || '',
        designation: newDesignation
      };
      updatedContacts.push(newContact);
    } else {
      // Mettre à jour le contact existant avec le bon typage
      const updatedContact = {
        ...updatedContacts[0],
        designation: newDesignation
      };
      updatedContacts[0] = updatedContact as typeof updatedContacts[0];
    }
    
    // Mettre à jour formData avec la nouvelle désignation et les contacts synchronisés
    setFormData((prev: ExtendedMarkerData) => ({
      ...prev,
      contacts: updatedContacts
    }));
  };

  return (
    <DesignationField>
      <FaUserTag className="field-icon" size={14} />
      <Select
        options={DESIGNATION_OPTIONS}
        value={currentDesignation}
        onChange={handleChange}
        disabled={isReadOnly}
        error={error}
        placeholder="Sélectionner une désignation"
      />
    </DesignationField>
  );
};
